import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "jc_admin_token";

const SERVICE_LABELS = {
  kitchen: "Kitchen",
  bathroom: "Bathroom",
  deck: "Deck / Outdoor",
  painting: "Painting",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);

  const fetchEnquiries = async (t) => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEnquiries(data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Could not load enquiries.");
      }
    }
  };

  useEffect(() => {
    if (token) fetchEnquiries(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center px-4">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-2xl border border-line bg-surface p-8"
        >
          <div className="flex flex-col gap-2">
            <Logo className="h-12 w-auto rounded-md" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-copper">Admin</span>
          </div>
          <p className="mt-3 text-sm text-cream/55">Sign in to view project enquiries.</p>
          <input
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className="mt-6 w-full rounded-xl border border-line bg-ink px-4 py-3.5 text-sm text-cream outline-none transition-colors focus:border-copper"
          />
          <input
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="mt-4 w-full rounded-xl border border-line bg-ink px-4 py-3.5 text-sm text-cream outline-none transition-colors focus:border-copper"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full rounded-full bg-copper px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-copper-hover disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="mx-auto min-h-screen max-w-7xl px-4 pb-20 pt-28 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-copper">Studio inbox</p>
          <h1 className="mt-2 font-display text-3xl font-semibold text-cream sm:text-4xl">Project enquiries</h1>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={() => fetchEnquiries(token)}
            className="flex items-center gap-2 rounded-full border border-line px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/70 transition-colors hover:border-copper hover:text-copper"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-line px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/70 transition-colors hover:border-red-400 hover:text-red-400"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>

      {enquiries === null ? (
        <p className="mt-16 text-center font-mono text-sm text-cream/50">Loading…</p>
      ) : enquiries.length === 0 ? (
        <p data-testid="admin-empty-state" className="mt-16 text-center font-mono text-sm text-cream/50">
          No enquiries yet. New submissions from the contact form will appear here.
        </p>
      ) : (
        <div data-testid="admin-enquiries-table" className="mt-10 overflow-x-auto rounded-2xl border border-line">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-line bg-surface font-mono text-[10px] uppercase tracking-[0.2em] text-copper">
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Service</th>
                <th className="px-5 py-4">Budget</th>
                <th className="px-5 py-4">Timeline</th>
                <th className="px-5 py-4">Message</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((q) => (
                <tr key={q.id} data-testid={`admin-enquiry-row-${q.id}`} className="border-b border-line/60 bg-ink/40 align-top transition-colors hover:bg-surface">
                  <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-cream/50">
                    {new Date(q.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4 font-medium text-cream">{q.name}</td>
                  <td className="px-5 py-4 text-cream/70">
                    <div>{q.email}</div>
                    {q.phone && <div className="text-xs text-cream/45">{q.phone}</div>}
                  </td>
                  <td className="px-5 py-4 text-cream/70">{SERVICE_LABELS[q.service] || q.service}</td>
                  <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-copper">
                    ${Math.round(q.budget / 1000)}k
                  </td>
                  <td className="px-5 py-4 text-cream/70">{q.timeline || "—"}</td>
                  <td className="max-w-xs px-5 py-4 text-cream/60">{q.message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
